// ChatBoxWeb.tsx — v17
//
// Nouveautés vs v16 :
//   - PRÉSENCE EN LIGNE (poussée par le backend, aucun appel répété) :
//       • À l'ouverture, l'événement `joined` apporte une VRAIE lecture du
//         statut de l'autre (other_presence : is_online + expires_in_ms).
//       • Ensuite le backend pousse `presence_update` par le socket déjà ouvert.
//       • Règle des 60 s côté frontend : un statut "en ligne" reste affiché
//         pendant expires_in_ms (60 s au maximum), puis passe hors ligne s'il
//         n'y a pas de nouveau signal. Un seul minuteur local (setTimeout),
//         aucun appel réseau.
//       • otherOnline est tri-état : true (en ligne), false (hors ligne),
//         null (statut caché ou pas encore lu → RIEN n'est affiché).
//   - INDICATEUR "ENREGISTRE UN VOCAL" (conversations privées) :
//       • L'enregistrement envoie UN signal `typing { activity: 'recording' }`
//         au début et UN à la fin (arrêt, annulation) — aucun renvoi répété.
//       • L'en-tête de l'autre affiche un design sans texte (micro, point rouge
//         qui pulse, barres animées) à la place de la ligne de statut.
//
// Nouveautés vs v15 (conservées de la v16) :
//   - Support du cache centralisé (voir hooks/useConversationsCache.ts) :
//       • État messages/hasMore initialisés depuis initialMessages/initialHasMore
//         (props) au lieu de tableaux vides → affichage INSTANTANÉ si la
//         conversation a déjà été visitée dans la session, pas d'écran de
//         chargement pour revenir sur une conversation déjà vue.
//       • loading initialisé à false si initialMessages est fourni et non
//         vide — le socket resynchronise en arrière-plan (événement
//         `joined`) sans bloquer l'affichage.
//       • Nouveau useEffect qui appelle onMessagesUpdate(conversationId,
//         messages, hasMore) à chaque changement de messages/hasMore, pour
//         que le parent (MessagesScreen) tienne son cache à jour en continu
//         — pas seulement au démontage, qui n'arrive plus systématiquement
//         maintenant que ChatBox peut rester monté.
//   - Type Message importé depuis ChatBoxTypes (source de vérité unique),
//     l'interface locale dupliquée est supprimée.
//
// Le reste (sockets, envoi optimiste, édition, media, action sheet) est
// inchangé par rapport à v15.
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Icônes, couleurs, IDs, endpoints, clés socket et tous les console.error
// restent codés en dur, inchangés. Racine de traduction partagée avec
// ChatBoxMobile.tsx : "chatBox".

import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TouchableOpacity,
  TextInput, ActivityIndicator, KeyboardAvoidingView,
  SafeAreaView, Modal, Image, Dimensions, Animated,
} from 'react-native';
import { Ionicons }       from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { io, Socket }     from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import type { ChatBoxProps, Message } from './ChatBoxTypes';

const WS_BASE   = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const MAX_RETRY = 3;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function detectMediaType(mimeType: string, fileName: string): string {
  const mime = mimeType.toLowerCase();
  if (mime.startsWith('image/'))  return 'image';
  if (mime.startsWith('video/'))  return 'video';
  if (mime.startsWith('audio/'))  return 'audio';
  if (mime === 'application/pdf' || mime.includes('word') || mime.includes('excel') ||
      mime.includes('powerpoint') || mime.includes('spreadsheet') ||
      mime.includes('presentation') || mime === 'text/plain' || mime === 'text/csv') return 'document';
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (['jpg','jpeg','png','webp','gif','bmp','tiff','svg','heic','heif'].includes(ext)) return 'image';
  if (['mp4','mov','webm','avi','mkv','m4v','3gp'].includes(ext))                        return 'video';
  if (['mp3','aac','wav','ogg','m4a','flac','opus','weba'].includes(ext))                return 'audio';
  if (['pdf','doc','docx','xls','xlsx','ppt','pptx','txt','csv'].includes(ext))          return 'document';
  return 'file';
}

function resolveMimeType(uri: string, mimeType?: string | null): string {
  if (mimeType) return mimeType;
  const ext = uri.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    jpg:'image/jpeg', jpeg:'image/jpeg', png:'image/png', webp:'image/webp',
    gif:'image/gif', heic:'image/heic', heif:'image/heif',
    mp4:'video/mp4', mov:'video/quicktime', webm:'video/webm',
    avi:'video/x-msvideo', mkv:'video/x-matroska',
    mp3:'audio/mpeg', aac:'audio/aac', wav:'audio/wav',
    ogg:'audio/ogg', m4a:'audio/x-m4a', flac:'audio/flac', pdf:'application/pdf',
  };
  return map[ext] ?? 'application/octet-stream';
}

function formatDuration(seconds: number): string {
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}

// ─── Lecteurs media web via createElement ────────────────────────────────────

function AudioPlayerWeb({ url, fromMe }: { url: string; fromMe: boolean }) {
  return (
    <View style={[S.audioWrap, fromMe ? S.audioWrapMe : S.audioWrapThem]}>
      <Ionicons
        name="musical-notes-outline"
        size={14}
        color={fromMe ? '#E0D0FF' : '#8A2BE2'}
        style={{ marginRight: 6 }}
      />
      {React.createElement('audio', {
        controls: true,
        src: url,
        style: { flex: 1, height: 32, minWidth: 160, outline: 'none' } as any,
      })}
    </View>
  );
}

function VideoPlayerWeb({ url }: { url: string }) {
  return (
    <View style={{ borderRadius: 10, overflow: 'hidden' }}>
      {React.createElement('video', {
        controls: true,
        src: url,
        style: { width: 220, height: 160, borderRadius: 10, display: 'block' } as any,
      })}
    </View>
  );
}

// ─── Indicateur "l'autre enregistre un vocal" (design sans texte) ─────────────
// Point rouge qui pulse + micro + trois barres façon onde sonore.
// Affiché dans l'en-tête à la place de la ligne de statut.

function RecordingIndicator() {
  const dot  = useRef(new Animated.Value(1)).current;
  const bars = useRef([0, 1, 2].map(() => new Animated.Value(0.35))).current;

  useEffect(() => {
    const loops = [
      Animated.loop(Animated.sequence([
        Animated.timing(dot, { toValue: 0.2, duration: 500, useNativeDriver: false }),
        Animated.timing(dot, { toValue: 1,   duration: 500, useNativeDriver: false }),
      ])),
      ...bars.map((b, i) =>
        Animated.loop(Animated.sequence([
          Animated.delay(i * 120),
          Animated.timing(b, { toValue: 1,    duration: 300, useNativeDriver: false }),
          Animated.timing(b, { toValue: 0.35, duration: 300, useNativeDriver: false }),
        ]))
      ),
    ];
    loops.forEach(l => l.start());
    return () => loops.forEach(l => l.stop());
  }, []);

  return (
    <View style={S.recIndicator}>
      <Animated.View style={[S.recDot, { opacity: dot }]} />
      <Ionicons name="mic" size={13} color="#FFD6D6" />
      <View style={S.recBars}>
        {bars.map((b, i) => (
          <Animated.View key={i} style={[S.recBar, { transform: [{ scaleY: b }] }]} />
        ))}
      </View>
    </View>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function ChatBoxWeb({
  conversationId, conversationType, userId, userName = 'Moi',
  userRole = 'user', accessToken,
  otherUser, groupName, memberCount,
  initialMessages, initialHasMore, onMessagesUpdate,
  onBack, onNewMessage, onProfilePress,
}: ChatBoxProps) {
  const { t } = useTranslation();

  // ── Hydratation depuis le cache (v16) ─────────────────────────────────────
  // Si initialMessages est fourni et non vide, on affiche immédiatement ce
  // contenu et on ne bloque pas sur `loading` — le socket resynchronisera
  // silencieusement via l'événement `joined`.
  const hasCachedData = !!(initialMessages && initialMessages.length > 0);

  const [messages,        setMessages]        = useState<Message[]>(initialMessages ?? []);
  const [input,           setInput]           = useState('');
  const [loading,         setLoading]         = useState(!hasCachedData);
  const [uploading,       setUploading]       = useState(false);
  const [otherTyping,     setOtherTyping]     = useState(false);
  // v17 : l'autre enregistre un vocal (conversations privées)
  const [otherRecording,  setOtherRecording]  = useState(false);
  // v17 : tri-état — true = en ligne, false = hors ligne,
  //       null = statut caché ou pas encore lu (rien n'est affiché).
  //       La valeur initiale est volontairement null : la vraie lecture arrive
  //       avec l'événement `joined` à l'ouverture.
  const [otherOnline,     setOtherOnline]     = useState<boolean | null>(null);
  const [hasMore,         setHasMore]         = useState(initialHasMore ?? false);
  const [loadingMore,     setLoadingMore]     = useState(false);
  const [currentRole,     setCurrentRole]     = useState(userRole);
  const [replyingTo,      setReplyingTo]      = useState<Message | null>(null);
  const [replyVisible,    setReplyVisible]    = useState(true);
  const [actionMsg,       setActionMsg]       = useState<Message | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [fullscreenUrl,   setFullscreenUrl]   = useState<string | null>(null);
  const [isOffline,       setIsOffline]       = useState(false);

  // ✦ état édition
  const [editingMsg,      setEditingMsg]      = useState<Message | null>(null);
  // Rôle de membre dans le groupe ('admin'|'moderator'|'member') — distinct du rôle d'abonnement
  const [groupMemberRole, setGroupMemberRole] = useState<string>('member');

  // ── Enregistrement vocal ──────────────────────────────────────────────────
  const [isRecording,       setIsRecording]       = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef   = useRef<MediaRecorder | null>(null);
  const recordedChunksRef  = useRef<Blob[]>([]);
  const recordingTimerRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingDotAnim   = useRef(new Animated.Value(1)).current;

  // ── Prévisualisation audio post-enregistrement ────────────────────────────
  const [recordedBlob,        setRecordedBlob]        = useState<Blob | null>(null);
  const [recordedBlobUrl,     setRecordedBlobUrl]      = useState<string | null>(null);
  const [previewPlaying,      setPreviewPlaying]       = useState(false);
  const [previewCurrentTime,  setPreviewCurrentTime]   = useState(0);
  const [previewDuration,     setPreviewDuration]      = useState(0);
  const recordingDurationRef  = useRef(0);
  const previewAudioRef       = useRef<HTMLAudioElement | null>(null);

  const scrollRef      = useRef<ScrollView>(null);
  const socketRef      = useRef<Socket | null>(null);
  const typingTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef    = useRef(false);
  const imgTapRef      = useRef<{ url: string; time: number } | null>(null);
  const lastTapRef     = useRef<{ id: string; time: number } | null>(null);
  const fileInputRef   = useRef<any>(null);

  // ✦ ref pour accès dans les callbacks socket
  const editingMsgRef  = useRef<Message | null>(null);

  const pendingPayloads = useRef<Map<string, any>>(new Map());
  const pendingQueue    = useRef<string[]>([]);
  const retryCounts     = useRef<Map<string, number>>(new Map());
  const isSendingRef    = useRef(false);

  // ── Présence de l'autre (v17) ─────────────────────────────────────────────
  // Minuteur LOCAL unique : il ne fait aucun appel réseau. Il ne sert qu'à
  // faire passer le statut "en ligne" à "hors ligne" quand les 60 s sont
  // écoulées sans nouveau signal.
  const presenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // isOnline = true  → en ligne pendant expiresInMs (60 s au maximum), puis hors ligne
  // isOnline = false → hors ligne tout de suite
  // isOnline = null  → statut caché : rien n'est affiché, minuteur annulé
  const applyPresence = (isOnline: boolean | null, expiresInMs = 0) => {
    if (presenceTimerRef.current) {
      clearTimeout(presenceTimerRef.current);
      presenceTimerRef.current = null;
    }
    if (isOnline === true) {
      if (expiresInMs <= 0) { setOtherOnline(false); return; }
      setOtherOnline(true);
      presenceTimerRef.current = setTimeout(() => {
        presenceTimerRef.current = null;
        setOtherOnline(false);
      }, expiresInMs);
      return;
    }
    setOtherOnline(isOnline);
  };

  // ── Sync rôle depuis le parent ────────────────────────────────────────────
  useEffect(() => {
    setCurrentRole(userRole);
  }, [userRole]);

  // ✦ sync ref édition
  useEffect(() => {
    editingMsgRef.current = editingMsg;
  }, [editingMsg]);

  // ── Remontée du cache au parent (v16) ─────────────────────────────────────
  // À chaque changement de messages/hasMore, on informe le parent pour qu'il
  // tienne son cache à jour — indépendamment du montage/démontage.
  useEffect(() => {
    onMessagesUpdate?.(conversationId, messages, hasMore);
  }, [messages, hasMore, conversationId, onMessagesUpdate]);

  // Accès premium : userpro uniquement — cohérent avec canSendMedia() backend
  const isPro = currentRole === 'userpro';

  // ── Animation point rouge enregistrement ─────────────────────────────────
  useEffect(() => {
    if (isRecording) {
      Animated.loop(Animated.sequence([
        Animated.timing(recordingDotAnim, { toValue: 0, duration: 500, useNativeDriver: false }),
        Animated.timing(recordingDotAnim, { toValue: 1, duration: 500, useNativeDriver: false }),
      ])).start();
    } else {
      recordingDotAnim.stopAnimation();
      recordingDotAnim.setValue(1);
    }
  }, [isRecording]);

  // ── Nettoyage global au démontage ─────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      if (recordedBlobUrl) URL.revokeObjectURL(recordedBlobUrl);
      if (presenceTimerRef.current) {
        clearTimeout(presenceTimerRef.current);
        presenceTimerRef.current = null;
      }
    };
  }, []);

  // ─── Socket ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!accessToken) return;
    const socket = conversationType === 'private' ? buildPrivateSocket() : buildGroupSocket();
    socketRef.current = socket;
    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      if (typingTimer.current)       clearTimeout(typingTimer.current);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (presenceTimerRef.current)  { clearTimeout(presenceTimerRef.current); presenceTimerRef.current = null; }
      pendingQueue.current = [];
      pendingPayloads.current.clear();
      retryCounts.current.clear();
    };
  }, [conversationId, accessToken]);

  // ─── Confirmation optimiste ───────────────────────────────────────────────

  const confirmOptimistic = (raw: any) => {
    const confirmed: Message = { ...normalizeMsg(raw), _pending: false, _failed: false };
    let matchedTempId: string | undefined;
    for (const [tempId, payload] of pendingPayloads.current.entries()) {
      const contentMatch = (payload.content ?? null) === (confirmed.content ?? null);
      const mediaMatch   = (payload.media_url ?? null) === (confirmed.media_url ?? null);
      if (contentMatch && mediaMatch) { matchedTempId = tempId; break; }
    }
    if (matchedTempId) {
      const idx = pendingQueue.current.indexOf(matchedTempId);
      if (idx === -1) {
        setMessages(prev => prev.find(m => m.id === confirmed.id) ? prev : [...prev, confirmed]);
        return;
      }
      pendingQueue.current.splice(idx, 1);
      pendingPayloads.current.delete(matchedTempId);
      retryCounts.current.delete(matchedTempId);
      setMessages(prev => prev.map(m => m._tempId === matchedTempId ? confirmed : m));
    } else {
      setMessages(prev => prev.find(m => m.id === confirmed.id) ? prev : [...prev, confirmed]);
    }
  };

  const handleServerError = () => {
    const tempId = pendingQueue.current[0];
    if (!tempId) return;
    const count = (retryCounts.current.get(tempId) ?? 0) + 1;
    retryCounts.current.set(tempId, count);
    if (count >= MAX_RETRY) {
      pendingQueue.current.shift();
      pendingPayloads.current.delete(tempId);
      retryCounts.current.delete(tempId);
      setMessages(prev => prev.map(m =>
        m._tempId === tempId ? { ...m, _pending: false, _failed: true } : m
      ));
    } else {
      const payload = pendingPayloads.current.get(tempId);
      if (payload) socketRef.current?.emit('send_message', payload);
    }
  };

  const flushPendingOnReconnect = (socket: Socket) => {
    setIsOffline(false);
    for (const tempId of pendingQueue.current) {
      const payload = pendingPayloads.current.get(tempId);
      if (payload) socket.emit('send_message', payload);
    }
  };

  const handleNewMessage = (raw: any) => {
    const msg = normalizeMsg(raw?.message ?? raw);
    if (msg.senderId === userId) {
      let matched = false;
      for (const [, payload] of pendingPayloads.current.entries()) {
        if ((payload.content ?? null) === (msg.content ?? null) &&
            (payload.media_url ?? null) === (msg.media_url ?? null)) { matched = true; break; }
      }
      if (matched) confirmOptimistic(raw?.message ?? raw);
      else setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]);
      scrollToEnd();
      return;
    }
    setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]);
    onNewMessage?.();
    scrollToEnd();
  };

  // ─── Socket privé ─────────────────────────────────────────────────────────

  const buildPrivateSocket = (): Socket => {
    const socket = io(`${WS_BASE}/private-chat`, { transports: ['websocket'], reconnectionDelay: 2000 });

    socket.on('connect', () => {
      socket.emit('join_conversation', { conversation_id: conversationId, user_id: userId, access_token: accessToken });
      flushPendingOnReconnect(socket);
    });
    socket.on('disconnect', () => setIsOffline(true));

    // Resynchronisation silencieuse : si des messages étaient déjà affichés
    // depuis le cache, on remplace par la version fraîche du serveur sans
    // repasser par un écran de chargement (loading reste déjà à false).
    // v17 : `joined` apporte aussi la VRAIE lecture du statut de l'autre.
    socket.on('joined', (data: {
      messages: Message[]; has_more: boolean; user_role?: string;
      other_presence?: { user_id: string; is_online: boolean | null; expires_in_ms: number } | null;
    }) => {
      setMessages((data.messages ?? []).map(normalizeMsg));
      setHasMore(data.has_more ?? false);
      if (data.user_role) setCurrentRole(data.user_role);
      if (data.other_presence) {
        applyPresence(data.other_presence.is_online, data.other_presence.expires_in_ms);
      }
      setLoading(false);
      scrollToEnd();
    });

    // v17 : le backend pousse chaque changement de présence de l'autre
    socket.on('presence_update', (data: { user_id: string; is_online: boolean | null; expires_in_ms?: number }) => {
      if (data.user_id !== otherUser?.id) return;
      applyPresence(data.is_online, data.expires_in_ms ?? 0);
    });

    socket.on('message_sent', (data: { message: Message }) => {
      confirmOptimistic(data.message);
      scrollToEnd();
    });
    socket.on('new_message', handleNewMessage);

    socket.on('more_messages', (data: { messages: Message[]; has_more: boolean }) => {
      setMessages(prev => [...(data.messages ?? []).map(normalizeMsg), ...prev]);
      setHasMore(data.has_more ?? false);
      setLoadingMore(false);
    });

    // v17 : activity = 'typing' (clavier) ou 'recording' (note vocale)
    socket.on('typing_status', (data: { user_id: string; is_typing: boolean; activity?: 'typing' | 'recording' }) => {
      if (data.user_id === userId) return;
      const recording = data.is_typing && data.activity === 'recording';
      setOtherRecording(recording);
      setOtherTyping(data.is_typing && !recording);
    });

    socket.on('message_deleted', (data: { message_id: string }) =>
      setMessages(prev => prev.map(m =>
        m.id === data.message_id
          ? { ...m, content: null, media_url: null, deletedAt: new Date().toISOString() }
          : m
      ))
    );

    socket.on('message_edited', (data: { message_id: string; content: string; edited_at: string }) => {
      setMessages(prev => prev.map(m =>
        m.id === data.message_id
          ? { ...m, content: data.content, editedAt: data.edited_at }
          : m
      ));
      if (editingMsgRef.current?.id === data.message_id) {
        cancelEditing();
      }
    });

    socket.on('edit_error', (data: { message: string; code?: string }) => {
      alert(data.message);
      const fatal = ['ALREADY_READ', 'RLS_DENIED', 'NOT_FOUND', 'ALREADY_DELETED'];
      if (fatal.includes(data.code ?? '')) cancelEditing();
    });

    socket.on('messages_read', (data: { reader_id: string }) => {
      if (data.reader_id !== userId) {
        setMessages(prev => prev.map(m =>
          m.senderId === userId ? { ...m, isRead: true } : m
        ));
      }
    });

    socket.on('error', () => handleServerError());
    return socket;
  };

  // ─── Socket groupe ────────────────────────────────────────────────────────

  const buildGroupSocket = (): Socket => {
    const socket = io(`${WS_BASE}/group-chat`, { transports: ['websocket'], reconnectionDelay: 2000 });

    socket.on('connect', () => {
      socket.emit('join_group', { group_id: conversationId, user_id: userId, access_token: accessToken });
      flushPendingOnReconnect(socket);
    });
    socket.on('disconnect', () => setIsOffline(true));

    socket.on('joined', (data: { messages: Message[]; group: any; members: any[] }) => {
      const msgs = data?.messages ?? [];
      setMessages(msgs.map(normalizeMsg));
      setHasMore(msgs.length >= 10);
      const me = (data.members ?? []).find((m: any) => m.user_id === userId);
      if (me?.role) setGroupMemberRole(me.role);
      setLoading(false);
      scrollToEnd();
    });

    socket.on('message_sent', (data: { message: Message }) => {
      confirmOptimistic(data.message);
      scrollToEnd();
    });
    socket.on('new_message', handleNewMessage);

    socket.on('more_messages', (data: { messages: Message[]; has_more: boolean }) => {
      setMessages(prev => [...(data.messages ?? []).map(normalizeMsg), ...prev]);
      setHasMore(data.has_more ?? false);
      setLoadingMore(false);
    });

    socket.on('typing_status', (data: { user_id: string; is_typing: boolean }) => {
      if (data.user_id !== userId) setOtherTyping(data.is_typing);
    });

    socket.on('message_deleted', (data: { message_id: string }) =>
      setMessages(prev => prev.map(m =>
        m.id === data.message_id
          ? { ...m, content: null, media_url: null, deletedAt: new Date().toISOString() }
          : m
      ))
    );

    socket.on('visibility_changed', (data: { message_id: string; is_visible: boolean }) =>
      setMessages(prev => prev.map(m =>
        m.id === data.message_id ? { ...m, is_visible: data.is_visible } : m
      ))
    );

    socket.on('message_edited', (data: { message_id: string; content: string; edited_at: string }) => {
      setMessages(prev => prev.map(m =>
        m.id === data.message_id
          ? { ...m, content: data.content, editedAt: data.edited_at }
          : m
      ));
      if (editingMsgRef.current?.id === data.message_id) {
        cancelEditing();
      }
    });

    socket.on('edit_error', (data: { message: string; code?: string }) => {
      alert(data.message);
      const fatal = ['ALREADY_READ', 'RLS_DENIED', 'NOT_FOUND', 'ALREADY_DELETED'];
      if (fatal.includes(data.code ?? '')) cancelEditing();
    });

    socket.on('messages_read', (data: { reader_id: string }) => {
      if (data.reader_id !== userId) {
        setMessages(prev => prev.map(m =>
          m.senderId === userId ? { ...m, isRead: true } : m
        ));
      }
    });

    socket.on('error', () => handleServerError());
    return socket;
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const scrollToEnd = () =>
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 100);

  const loadMore = () => {
    if (loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    const key = conversationType === 'private' ? 'conversation_id' : 'group_id';
    socketRef.current?.emit('load_more', {
      [key]: conversationId, user_id: userId, access_token: accessToken, before: messages[0]?.createdAt,
    });
  };

  const buildPayload = (
    content: string | null, mediaUrl?: string, mediaType?: string,
    replyId?: string | null, isVisible?: boolean
  ) => conversationType === 'private'
    ? { conversation_id: conversationId, user_id: userId, access_token: accessToken, content: content || null, type: mediaType ?? 'text', media_url: mediaUrl ?? null }
    : { group_id: conversationId, user_id: userId, access_token: accessToken, content: content || null, type: mediaType ?? 'text', media_url: mediaUrl ?? null, reply_to_id: replyId ?? null, is_visible: isVisible ?? true };

  // ─── Envoi optimiste ──────────────────────────────────────────────────────

  const sendMessage = (mediaUrl?: string, mediaType?: string) => {
    if (isSendingRef.current) return;
    const content = input.trim();
    if (!content && !mediaUrl) return;
    isSendingRef.current = true;
    setInput('');
    const reply = replyingTo;
    setReplyingTo(null);
    if (isTypingRef.current) { isTypingRef.current = false; emitTyping(false); }
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const optimistic: Message = {
      id: tempId, _tempId: tempId, _pending: true, _failed: false,
      content: content || null, media_url: mediaUrl ?? null,
      senderId: userId, isFromMe: true, senderName: userName, senderAvatar: null,
      createdAt: new Date().toISOString(), isRead: false, deletedAt: null, editedAt: null,
      reply_to_id: reply?.id ?? null, is_visible: reply ? replyVisible : true, is_private_reply: false,
      replyTo: reply ? {
        id: reply.id, sender_id: reply.senderId, content: reply.content,
        sender_nom: reply.senderName.split(' ').slice(1).join(' ') || reply.senderName,
        sender_prenom: reply.senderName.split(' ')[0] ?? '',
      } : null,
      mediaType: mediaType ?? null,
    };
    setMessages(prev => [...prev, optimistic]);
    pendingQueue.current.push(tempId);
    retryCounts.current.set(tempId, 0);
    scrollToEnd();
    const payload = buildPayload(content || null, mediaUrl, mediaType, reply?.id, reply ? replyVisible : true);
    pendingPayloads.current.set(tempId, payload);
    socketRef.current?.emit('send_message', payload);
    setTimeout(() => { isSendingRef.current = false; }, 300);
  };

  const retrySend = (msg: Message) => {
    if (!msg._tempId) return;
    const payload = buildPayload(msg.content, msg.media_url ?? undefined, msg.mediaType ?? undefined, msg.reply_to_id, msg.is_visible);
    setMessages(prev => prev.map(m =>
      m._tempId === msg._tempId ? { ...m, _pending: true, _failed: false } : m
    ));
    pendingQueue.current.push(msg._tempId);
    pendingPayloads.current.set(msg._tempId, payload);
    retryCounts.current.set(msg._tempId, 0);
    socketRef.current?.emit('send_message', payload);
  };

  // ✦ helpers édition ──────────────────────────────────────────────────

  const startEditing = (msg: Message) => {
    setEditingMsg(msg);
    editingMsgRef.current = msg;
    setInput(msg.content ?? '');
    setReplyingTo(null);
  };

  const cancelEditing = () => {
    setEditingMsg(null);
    editingMsgRef.current = null;
    setInput('');
  };

  const submitEdit = () => {
    const editing = editingMsgRef.current;
    if (!editing || !input.trim()) return;
    socketRef.current?.emit('edit_message', {
      message_id:   editing.id,
      user_id:      userId,
      access_token: accessToken,
      content:      input.trim(),
    });
  };

  // ─── Typing ───────────────────────────────────────────────────────────────

  const emitTyping = (is_typing: boolean) => {
    if (conversationType === 'private')
      socketRef.current?.emit('typing', { conversation_id: conversationId, user_id: userId, is_typing });
    else
      socketRef.current?.emit('typing', { group_id: conversationId, user_id: userId, user_name: userName, is_typing });
  };

  // v17 : signal "enregistre un vocal" — UN envoi au début, UN à la fin
  // (aucun renvoi répété). Conversations privées uniquement.
  const emitRecording = (recording: boolean) => {
    if (conversationType !== 'private') return;
    socketRef.current?.emit('typing', {
      conversation_id: conversationId, user_id: userId,
      is_typing: recording, activity: 'recording',
    });
  };

  const handleTyping = (text: string) => {
    setInput(text);
    if (!editingMsg) {
      if (text.length > 0 && !isTypingRef.current) { isTypingRef.current = true; emitTyping(true); }
      if (typingTimer.current) clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => { isTypingRef.current = false; emitTyping(false); }, 2000);
    }
  };

  // ─── Actions messages ─────────────────────────────────────────────────────

  const deleteMessage    = (msg: Message) =>
    socketRef.current?.emit('delete_message', { message_id: msg.id, user_id: userId, access_token: accessToken });

  const toggleVisibility = (msg: Message) => {
    if (conversationType !== 'group') return;
    socketRef.current?.emit('toggle_visibility', { message_id: msg.id, user_id: userId, access_token: accessToken });
  };

  const handleLongPress = (msg: Message) => {
    if (msg._pending || msg._failed) return;
    setActionMsg(msg); setShowActionSheet(true);
  };

  const handleTap = (msg: Message) => {
    if (conversationType !== 'group') return;
    const now = Date.now(); const last = lastTapRef.current;
    if (last && last.id === msg.id && now - last.time < 350) {
      lastTapRef.current = null;
      if (msg.senderId === userId && msg.reply_to_id) toggleVisibility(msg);
    } else lastTapRef.current = { id: msg.id, time: now };
  };

  // ─── Upload fichier (image/vidéo/doc) ────────────────────────────────────

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await uploadAndSend(file);
  };

  const uploadAndSend = async (file: File) => {
    const mimeType = file.type || 'application/octet-stream';
    const fileName = file.name;
    setUploading(true);
    try {
      const urlData = await fetch(`${WS_BASE}/media`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          function: 'getUploadUrl', user_id: userId, access_token: accessToken,
          file_name: fileName, file_type: mimeType, context: 'media-premium',
        }),
      }).then(r => r.json());
      if (!urlData.success) { setUploading(false); alert(urlData.error ?? t('chatBox.errors.uploadUrlGeneration')); return; }
      const { signed_url, path, bucket } = urlData;
      const uploadRes = await fetch(signed_url, { method: 'PUT', headers: { 'Content-Type': mimeType }, body: file });
      if (!uploadRes.ok) { setUploading(false); alert(t('chatBox.errors.uploadFailed')); return; }
      const confirmData = await fetch(`${WS_BASE}/media`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ function: 'confirmUpload', user_id: userId, access_token: accessToken, path, bucket }),
      }).then(r => r.json());
      setUploading(false);
      if (!confirmData.success) { alert(confirmData.error ?? t('chatBox.errors.uploadConfirmation')); return; }
      sendMessage(confirmData.url, detectMediaType(mimeType, fileName));
    } catch (err) {
      setUploading(false);
      console.error('[ChatBoxWeb] upload:', err);
      alert(t('chatBox.errors.uploadFailed'));
    }
  };

  // ─── Enregistrement vocal via MediaRecorder ───────────────────────────────

  const startVoiceRecording = async () => {
    try {
      const stream = await (navigator as any).mediaDevices.getUserMedia({ audio: true });
      const options = (window as any).MediaRecorder?.isTypeSupported?.('audio/webm;codecs=opus')
        ? { mimeType: 'audio/webm;codecs=opus' }
        : (window as any).MediaRecorder?.isTypeSupported?.('audio/webm')
          ? { mimeType: 'audio/webm' }
          : {};
      const mr = new (window as any).MediaRecorder(stream, options) as MediaRecorder;
      recordedChunksRef.current = [];
      mr.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.start(100);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      // v17 : l'autre voit "enregistre un vocal" (un seul signal au début)
      emitRecording(true);
      setRecordingDuration(0);
      recordingDurationRef.current = 0;
      recordingTimerRef.current = setInterval(() => {
        recordingDurationRef.current += 1;
        setRecordingDuration(d => d + 1);
      }, 1000);
    } catch {
      alert(t('chatBox.errors.microphonePermission'));
    }
  };

  const stopVoiceRecording = (cancel = false) => {
    // v17 : fin de l'enregistrement (arrêt ou annulation) → l'indicateur s'éteint
    emitRecording(false);
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    setIsRecording(false);
    setRecordingDuration(0);

    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === 'inactive') return;

    if (cancel) {
      mr.stop();
      (mr as any).stream?.getTracks?.().forEach((t: MediaStreamTrack) => t.stop());
      mediaRecorderRef.current = null;
      recordedChunksRef.current = [];
      return;
    }

    mr.onstop = () => {
      (mr as any).stream?.getTracks?.().forEach((t: MediaStreamTrack) => t.stop());
      mediaRecorderRef.current = null;
      if (recordedChunksRef.current.length === 0) return;

      const mimeType = mr.mimeType || 'audio/webm';
      const blob     = new Blob(recordedChunksRef.current, { type: mimeType });
      const url      = URL.createObjectURL(blob);

      const audio    = document.createElement('audio');
      audio.src      = url;
      audio.preload  = 'metadata';
      audio.onloadedmetadata = () => {
        const dur = isFinite(audio.duration) ? audio.duration : recordingDurationRef.current;
        setPreviewDuration(dur);
      };
      audio.ontimeupdate = () => setPreviewCurrentTime(audio.currentTime);
      audio.onended      = () => setPreviewPlaying(false);

      previewAudioRef.current = audio;
      setRecordedBlob(blob);
      setRecordedBlobUrl(url);
      setPreviewPlaying(false);
      setPreviewCurrentTime(0);
      setPreviewDuration(recordingDurationRef.current);
    };
    mr.stop();
  };

  // ─── Contrôles du lecteur de prévisualisation ─────────────────────────────

  const togglePreviewPlay = () => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    if (previewPlaying) {
      audio.pause();
      setPreviewPlaying(false);
    } else {
      audio.play().catch(() => {});
      setPreviewPlaying(true);
    }
  };

  const seekPreview = (seconds: number) => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    audio.currentTime = seconds;
    setPreviewCurrentTime(seconds);
  };

  const discardRecording = () => {
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    if (recordedBlobUrl) URL.revokeObjectURL(recordedBlobUrl);
    setRecordedBlob(null);
    setRecordedBlobUrl(null);
    setPreviewPlaying(false);
    setPreviewCurrentTime(0);
    setPreviewDuration(0);
  };

  const sendRecordedAudio = async () => {
    if (!recordedBlob) return;
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    const blob     = recordedBlob;
    const blobUrl  = recordedBlobUrl;
    setRecordedBlob(null);
    setRecordedBlobUrl(null);
    setPreviewPlaying(false);
    setPreviewCurrentTime(0);
    setPreviewDuration(0);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    const mimeType = blob.type || 'audio/webm';
    const ext      = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'mp4' : 'webm';
    const file     = new File([blob], `voice_${Date.now()}.${ext}`, { type: mimeType });
    await uploadAndSend(file);
  };

  // ─── Normalisation ────────────────────────────────────────────────────────

  const normalizeMsg = (msg: any): Message => ({
    id:               msg.id,
    content:          msg.content      ?? msg.text       ?? null,
    media_url:        msg.media_url    ?? msg.mediaUrl   ?? null,
    senderId:         msg.senderId     ?? msg.sender_id  ?? '',
    isFromMe:         msg.isFromMe     ?? msg.is_from_me ?? false,
    senderName:       msg.senderName   ?? msg.sender_name ?? (msg.sender ? `${msg.sender.prenom} ${msg.sender.nom}` : ''),
    senderAvatar:     msg.senderAvatar ?? msg.sender?.avatar_url ?? null,
    createdAt:        msg.createdAt    ?? msg.created_at ?? null,
    isRead:           msg.isRead       ?? msg.is_read    ?? false,
    deletedAt:        msg.deletedAt    ?? msg.deleted_at ?? null,
    editedAt:         msg.editedAt     ?? msg.edited_at  ?? null,
    reply_to_id:      msg.reply_to_id  ?? null,
    is_visible:       msg.is_visible   ?? true,
    is_private_reply: msg.is_private_reply ?? false,
    replyTo:          msg.reply_to     ?? msg.replyTo    ?? null,
    mediaType:        msg.mediaType    ?? msg.type       ?? null,
    _pending: false, _failed: false,
  });

  const fmtTime = (v: string | null | undefined): string => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };
  const isAuthor = (msg: Message) => msg.senderId === userId;

  // ─── Statut bulle ─────────────────────────────────────────────────────────

  const renderStatus = (msg: Message) => {
    if (!msg.isFromMe) return null;
    if (msg._pending) return <Text style={S.statusText}> ⏳</Text>;
    if (msg._failed) return (
      <TouchableOpacity onPress={() => retrySend(msg)} style={S.retryBtn}>
        <Ionicons name="refresh-outline" size={13} color="#FF6B6B" />
        <Text style={S.retryTxt}>{t('chatBox.retry')}</Text>
      </TouchableOpacity>
    );
    return <Text style={S.statusText}>{msg.isRead ? ' ✓✓' : ' ✓'}</Text>;
  };

  // ─── Rendu média ──────────────────────────────────────────────────────────

  const renderMediaContent = (msg: Message, fromMe: boolean) => {
    if (!msg.media_url) return null;
    const url  = msg.media_url;
    const type = (msg.mediaType && msg.mediaType !== 'text')
      ? msg.mediaType
      : detectMediaType(resolveMimeType(url), url);

    if (type === 'image') return (
      <TouchableOpacity activeOpacity={0.9} onPress={() => {
        const now = Date.now(); const last = imgTapRef.current;
        if (last && last.url === url && now - last.time < 350) {
          imgTapRef.current = null; setFullscreenUrl(url);
        } else imgTapRef.current = { url, time: now };
      }}>
        <Image source={{ uri: url }} style={S.mediaImage} resizeMode="cover" />
      </TouchableOpacity>
    );

    if (type === 'audio') return <AudioPlayerWeb url={url} fromMe={fromMe} />;
    if (type === 'video') return <VideoPlayerWeb url={url} />;

    const icons:  Record<string, string> = { document: 'document-text-outline', file: 'attach-outline' };
    const labels: Record<string, string> = { document: t('chatBox.media.document'), file: t('chatBox.media.file') };
    return (
      <View style={[S.mediaFileWrap, fromMe ? S.mediaFileWrapMe : S.mediaFileWrapThem]}>
        <View style={[S.mediaIconCircle, fromMe ? S.mediaIconCircleMe : S.mediaIconCircleThem]}>
          <Ionicons name={(icons[type] ?? 'attach-outline') as any} size={20} color={fromMe ? '#E0D0FF' : '#fff'} />
        </View>
        <Text style={[S.mediaFileName, fromMe ? S.mediaFileNameMe : S.mediaFileNameThem]} numberOfLines={1}>
          {url.split('/').pop()?.split('?')[0] ?? labels[type] ?? t('chatBox.media.file')}
        </Text>
      </View>
    );
  };

  // ─── Rendu bulle ──────────────────────────────────────────────────────────

  const renderBubble = (msg: Message) => {
    const fromMe  = msg.isFromMe;
    const deleted = !!msg.deletedAt;
    const isGroup = conversationType === 'group';
    return (
      <TouchableOpacity
        key={msg._tempId ?? msg.id}
        activeOpacity={0.85}
        onPress={() => handleTap(msg)}
        onLongPress={() => handleLongPress(msg)}
        style={[S.bubble, fromMe ? S.bubbleMe : S.bubbleThem, msg._failed ? S.bubbleFailed : null]}
      >
        {!fromMe && isGroup && !deleted && (
          <Text style={S.senderName}>{msg.senderName}</Text>
        )}
        {msg.replyTo && !deleted && (
          <View style={[S.replyBlock, fromMe ? S.replyBlockMe : S.replyBlockThem]}>
            <Text style={S.replyAuthor} numberOfLines={1}>
              {msg.replyTo.sender_prenom} {msg.replyTo.sender_nom}
            </Text>
            <Text style={S.replyContent} numberOfLines={2}>
              {msg.replyTo.content ?? t('chatBox.messageDeleted')}
            </Text>
          </View>
        )}
        {deleted
          ? <Text style={[S.msgText, S.msgDeleted]}>{t('chatBox.messageDeleted')}</Text>
          : msg.is_private_reply
            ? (
              <View style={S.privateReplyWrap}>
                <Ionicons name="lock-closed" size={12} color={fromMe ? '#E0D0FF' : '#999'} />
                <Text style={[S.msgText, fromMe ? S.msgTextMe : S.msgTextThem, { fontStyle: 'italic', marginLeft: 4 }]}>
                  {t('chatBox.privateReply')}
                </Text>
              </View>
            )
            : msg.media_url
              ? renderMediaContent(msg, fromMe)
              : <Text style={[S.msgText, fromMe ? S.msgTextMe : S.msgTextThem]}>{msg.content}</Text>
        }
        <View style={S.bubbleFooter}>
          {msg.editedAt && !deleted && (
            <Text style={[S.editedLabel, fromMe ? S.editedLabelMe : S.editedLabelThem]}>
              {t('chatBox.edited')}
            </Text>
          )}
          {isGroup && isAuthor(msg) && msg.reply_to_id && !deleted && (
            <Ionicons
              name={msg.is_visible ? 'eye-outline' : 'eye-off-outline'}
              size={11} color={fromMe ? '#E0D0FF' : '#aaa'} style={{ marginRight: 3 }}
            />
          )}
          <Text style={[S.msgTime, fromMe ? S.msgTimeMe : S.msgTimeThem]}>{fmtTime(msg.createdAt)}</Text>
          {renderStatus(msg)}
        </View>
      </TouchableOpacity>
    );
  };

  // ─── Barre de saisie ──────────────────────────────────────────────────────

  const renderInputBar = () => {

    if (recordedBlobUrl) {
      const maxDur = previewDuration > 0 ? previewDuration : (recordingDurationRef.current || 1);
      return (
        <View style={S.inputWrap}>
          <TouchableOpacity style={S.recordCancelBtn} onPress={discardRecording} activeOpacity={0.7}>
            <Ionicons name="trash-outline" size={22} color="#DC2626" />
          </TouchableOpacity>
          <TouchableOpacity style={S.previewPlayBtn} onPress={togglePreviewPlay} activeOpacity={0.7}>
            <Ionicons name={previewPlaying ? 'pause' : 'play'} size={20} color="#8A2BE2" />
          </TouchableOpacity>
          <View style={S.previewSliderWrap}>
            {React.createElement('input', {
              type:     'range',
              min:      0,
              max:      maxDur,
              step:     0.1,
              value:    previewCurrentTime,
              onChange: (e: any) => seekPreview(parseFloat(e.target.value)),
              style:    { width: '100%', accentColor: '#8A2BE2', cursor: 'pointer', height: 4 } as any,
            })}
            <View style={S.previewTimeRow}>
              <Text style={S.previewTimeText}>{formatDuration(previewCurrentTime)}</Text>
              <Text style={S.previewTimeText}>{formatDuration(maxDur)}</Text>
            </View>
          </View>
          <TouchableOpacity style={S.sendBtn} onPress={sendRecordedAudio} disabled={uploading} activeOpacity={0.7}>
            {uploading
              ? <ActivityIndicator size="small" color="#fff" />
              : <Ionicons name="send" size={20} color="#fff" />
            }
          </TouchableOpacity>
        </View>
      );
    }

    if (isRecording) return (
      <View style={S.inputWrap}>
        <TouchableOpacity style={S.recordCancelBtn} onPress={() => stopVoiceRecording(true)}>
          <Ionicons name="trash-outline" size={22} color="#DC2626" />
        </TouchableOpacity>
        <View style={S.recordingIndicator}>
          <Animated.View style={[S.recordingDot, { opacity: recordingDotAnim }]} />
          <Text style={S.recordingTimer}>{formatDuration(recordingDuration)}</Text>
          <Text style={S.recordingLabel}>{t('chatBox.recording.inProgress')}</Text>
        </View>
        <TouchableOpacity style={S.sendBtn} onPress={() => stopVoiceRecording(false)}>
          <Ionicons name="stop" size={20} color="#fff" />
        </TouchableOpacity>
      </View>
    );

    const isEditMode = !!editingMsg;
    return (
      <View style={S.inputWrap}>
        {isPro && !isEditMode && (
          <TouchableOpacity
            style={S.attachBtn}
            onPress={() => fileInputRef.current?.click()}
            disabled={uploading}
            activeOpacity={0.7}
          >
            {uploading
              ? <ActivityIndicator size="small" color="#8A2BE2" />
              : <Ionicons name="attach-outline" size={24} color="#8A2BE2" />
            }
          </TouchableOpacity>
        )}

        <TextInput
          style={[S.input, (!isPro && !isEditMode) && S.inputFull]}
          placeholder={isEditMode ? t('chatBox.input.editPlaceholder') : t('chatBox.input.placeholder')}
          placeholderTextColor="#aaa"
          value={input}
          onChangeText={handleTyping}
          multiline
          maxLength={1000}
          editable={!uploading}
        />

        {(input.trim() || isEditMode) ? (
          <TouchableOpacity
            style={[S.sendBtn, isEditMode ? S.sendBtnEdit : null]}
            onPress={() => isEditMode ? submitEdit() : sendMessage()}
            disabled={uploading || !input.trim()}
            activeOpacity={0.7}
          >
            {uploading
              ? <ActivityIndicator size="small" color="#fff" />
              : <Ionicons name={isEditMode ? 'checkmark' : 'send'} size={20} color="#fff" />
            }
          </TouchableOpacity>
        ) : isPro ? (
          <TouchableOpacity style={S.micBtn} onPress={startVoiceRecording} disabled={uploading} activeOpacity={0.7}>
            <Ionicons name="mic-outline" size={24} color="#8A2BE2" />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={[S.sendBtn, S.sendBtnDisabled]} disabled>
            <Ionicons name="send" size={20} color="#fff" />
          </TouchableOpacity>
        )}
      </View>
    );
  };

  // ─── Rendu principal ──────────────────────────────────────────────────────

  return (
    <SafeAreaView style={S.container}>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <LinearGradient colors={['#8A2BE2', '#4B0082']} style={S.header}>
        <TouchableOpacity onPress={onBack} style={S.backBtn}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        {conversationType === 'private' ? (
          <TouchableOpacity
            style={S.headerInfo}
            onPress={() => otherUser && onProfilePress?.(otherUser.id)}
            activeOpacity={onProfilePress ? 0.75 : 1}
          >
            <View style={S.avatar}>
              <Text style={S.avatarTxt}>{otherUser?.prenom.charAt(0)}{otherUser?.nom.charAt(0)}</Text>
              {/* v17 : point vert uniquement si en ligne (false et null : rien) */}
              {otherOnline === true && <View style={S.onlineDot} />}
            </View>
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={S.headerName}>{otherUser?.prenom} {otherUser?.nom}</Text>
              {/* v17 : priorité à l'enregistrement vocal, puis à la frappe,
                  puis au statut. Statut caché (null) → aucune ligne affichée. */}
              {otherRecording ? (
                <RecordingIndicator />
              ) : otherTyping ? (
                <Text style={S.headerStatus}>{t('chatBox.status.typing')}</Text>
              ) : otherOnline === true ? (
                <Text style={S.headerStatus}>{t('chatBox.status.online')}</Text>
              ) : otherOnline === false ? (
                <Text style={S.headerStatus}>{t('chatBox.status.offline')}</Text>
              ) : null}
            </View>
            {onProfilePress && <Ionicons name="chevron-forward" size={18} color="rgba(255,255,255,0.5)" />}
          </TouchableOpacity>
        ) : (
          <View style={S.headerInfo}>
            <View style={[S.avatar, { backgroundColor: '#FF8C00' }]}>
              <Ionicons name="people" size={20} color="#fff" />
            </View>
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={S.headerName}>{groupName}</Text>
              <Text style={S.headerStatus}>
                {otherTyping ? t('chatBox.status.someoneTyping') : t('chatBox.status.memberCount', { count: memberCount })}
              </Text>
            </View>
          </View>
        )}
      </LinearGradient>

      {/* ── Bannière hors-ligne ───────────────────────────────────────────── */}
      {isOffline && (
        <View style={S.offlineBanner}>
          <Ionicons name="cloud-offline-outline" size={15} color="#fff" />
          <Text style={S.offlineTxt}>
            {t('chatBox.offlineBanner')}
          </Text>
        </View>
      )}

      {/* ── Messages ─────────────────────────────────────────────────────── */}
      <ScrollView
        ref={scrollRef}
        style={S.msgs}
        contentContainerStyle={{ padding: 15, paddingBottom: 20 }}
      >
        {hasMore && !loading && (
          <TouchableOpacity onPress={loadMore} disabled={loadingMore} style={S.loadMoreBtn}>
            {loadingMore
              ? <Text style={S.loadMoreTxt}>{t('chatBox.loadingEllipsis')}</Text>
              : <><Ionicons name="chevron-up" size={16} color="#8A2BE2" /><Text style={S.loadMoreTxt}>{t('chatBox.loadPreviousMessages')}</Text></>
            }
          </TouchableOpacity>
        )}
        {loading ? (
          <View style={S.emptyWrap}><Text style={S.emptyText}>{t('chatBox.loadingData')}</Text></View>
        ) : messages.length === 0 ? (
          <View style={S.emptyWrap}>
            <Ionicons name="chatbubble-outline" size={60} color="#ccc" />
            <Text style={S.emptyTitle}>{t('chatBox.empty.title')}</Text>
            <Text style={S.emptySubtitle}>{t('chatBox.empty.subtitle')}</Text>
          </View>
        ) : messages.map(msg => renderBubble(msg))}
      </ScrollView>

      {/* ── Barre de réponse ─────────────────────────────────────────────── */}
      {replyingTo && !editingMsg && (
        <View style={S.replyBar}>
          <Ionicons
            name={replyVisible ? 'earth-outline' : 'lock-closed-outline'}
            size={14} color={replyVisible ? '#8A2BE2' : '#6B21A8'} style={{ marginRight: 6 }}
          />
          <View style={{ flex: 1 }}>
            <Text style={[S.replyBarAuthor, { color: replyVisible ? '#8A2BE2' : '#6B21A8' }]} numberOfLines={1}>
              {replyVisible ? t('chatBox.replyBar.publicPrefix') : t('chatBox.replyBar.privatePrefix')}{replyingTo.senderName}
            </Text>
            <Text style={S.replyBarText} numberOfLines={1}>
              {replyingTo.content ?? t('chatBox.messageDeleted')}
            </Text>
          </View>
          <TouchableOpacity onPress={() => { setReplyingTo(null); setReplyVisible(true); }} style={{ padding: 6 }}>
            <Ionicons name="close" size={18} color="#999" />
          </TouchableOpacity>
        </View>
      )}

      {/* ✦ Barre d'édition ─────────────────────────────────────────── */}
      {editingMsg && !recordedBlobUrl && !isRecording && (
        <View style={S.editBar}>
          <View style={S.editBarIconWrap}>
            <Ionicons name="pencil" size={14} color="#8A2BE2" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={S.editBarLabel}>{t('chatBox.editBar.label')}</Text>
            <Text style={S.editBarText} numberOfLines={1}>
              {editingMsg.content ?? ''}
            </Text>
          </View>
          <TouchableOpacity onPress={cancelEditing} style={{ padding: 6 }}>
            <Ionicons name="close" size={18} color="#999" />
          </TouchableOpacity>
        </View>
      )}

      {/* ── Barre de saisie ──────────────────────────────────────────────── */}
      <KeyboardAvoidingView behavior={undefined}>
        {renderInputBar()}
      </KeyboardAvoidingView>

      {/* ── Input fichier natif (invisible) ──────────────────────────────── */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* ── Action sheet ─────────────────────────────────────────────────── */}
      <Modal visible={showActionSheet} transparent animationType="fade" onRequestClose={() => setShowActionSheet(false)}>
        <TouchableOpacity style={S.modalOverlay} activeOpacity={1} onPress={() => setShowActionSheet(false)}>
          <View style={S.actionSheet}>
            {actionMsg && (<>

              {conversationType === 'group' && !actionMsg.deletedAt && !actionMsg.is_private_reply && (<>
                <TouchableOpacity style={S.actionItem} onPress={() => { setReplyingTo(actionMsg); setReplyVisible(true); setShowActionSheet(false); }}>
                  <View style={[S.actionIcon, { backgroundColor: '#EDE9FF' }]}>
                    <Ionicons name="earth-outline" size={20} color="#8A2BE2" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[S.actionTxt, { color: '#8A2BE2' }]}>{t('chatBox.actions.replyPublicly')}</Text>
                    <Text style={S.actionSubTxt}>{t('chatBox.actions.replyPubliclySub')}</Text>
                  </View>
                </TouchableOpacity>
                <TouchableOpacity style={S.actionItem} onPress={() => { setReplyingTo(actionMsg); setReplyVisible(false); setShowActionSheet(false); }}>
                  <View style={[S.actionIcon, { backgroundColor: '#F3E8FF' }]}>
                    <Ionicons name="lock-closed-outline" size={20} color="#6B21A8" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[S.actionTxt, { color: '#6B21A8' }]}>{t('chatBox.actions.replyPrivately')}</Text>
                    <Text style={S.actionSubTxt}>{t('chatBox.actions.replyPrivatelySub')}</Text>
                  </View>
                </TouchableOpacity>
              </>)}

              {conversationType === 'group' && isAuthor(actionMsg) && actionMsg.reply_to_id && !actionMsg.deletedAt && (
                <TouchableOpacity style={S.actionItem} onPress={() => { toggleVisibility(actionMsg); setShowActionSheet(false); }}>
                  <View style={[S.actionIcon, { backgroundColor: '#F5F5F5' }]}>
                    <Ionicons name={actionMsg.is_visible ? 'eye-off-outline' : 'eye-outline'} size={20} color="#555" />
                  </View>
                  <Text style={S.actionTxt}>{actionMsg.is_visible ? t('chatBox.actions.makePrivate') : t('chatBox.actions.makeVisible')}</Text>
                </TouchableOpacity>
              )}

              {isAuthor(actionMsg) && !actionMsg.deletedAt && !actionMsg._pending && !actionMsg._failed &&
               !actionMsg.isRead && !actionMsg.media_url && (
                <TouchableOpacity
                  style={S.actionItem}
                  onPress={() => { startEditing(actionMsg); setShowActionSheet(false); }}
                >
                  <View style={[S.actionIcon, { backgroundColor: '#ECFDF5' }]}>
                    <Ionicons name="pencil-outline" size={20} color="#059669" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[S.actionTxt, { color: '#059669' }]}>{t('chatBox.actions.edit')}</Text>
                    <Text style={S.actionSubTxt}>{t('chatBox.actions.editSub')}</Text>
                  </View>
                </TouchableOpacity>
              )}

              {isAuthor(actionMsg) && !actionMsg.deletedAt && !actionMsg._pending && !actionMsg._failed &&
               !actionMsg.isRead && (
                <TouchableOpacity style={S.actionItem} onPress={() => { deleteMessage(actionMsg); setShowActionSheet(false); }}>
                  <View style={[S.actionIcon, { backgroundColor: '#FEE2E2' }]}>
                    <Ionicons name="trash-outline" size={20} color="#DC2626" />
                  </View>
                  <Text style={[S.actionTxt, { color: '#DC2626' }]}>{t('chatBox.actions.delete')}</Text>
                </TouchableOpacity>
              )}

              <View style={S.actionDivider} />
              <TouchableOpacity style={S.actionItem} onPress={() => setShowActionSheet(false)}>
                <Text style={[S.actionTxt, { textAlign: 'center', color: '#999', flex: 1 }]}>{t('chatBox.actions.cancel')}</Text>
              </TouchableOpacity>
            </>)}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* ── Visionneuse plein écran ───────────────────────────────────────── */}
      <Modal visible={!!fullscreenUrl} transparent animationType="fade" onRequestClose={() => setFullscreenUrl(null)}>
        <TouchableOpacity style={S.fsOverlay} activeOpacity={1} onPress={() => {
          const now = Date.now(); const last = imgTapRef.current;
          if (last && last.url === fullscreenUrl && now - last.time < 350) {
            imgTapRef.current = null; setFullscreenUrl(null);
          } else imgTapRef.current = { url: fullscreenUrl!, time: now };
        }}>
          <Image source={{ uri: fullscreenUrl ?? '' }} style={S.fsImage} resizeMode="contain" />
          <TouchableOpacity style={S.fsCloseBtn} onPress={() => setFullscreenUrl(null)}>
            <Ionicons name="close-circle" size={36} color="rgba(255,255,255,0.85)" />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  container:           { flex: 1, backgroundColor: '#F5F5F5' },

  header:              { flexDirection: 'row', alignItems: 'center', paddingTop: 20, paddingBottom: 14, paddingHorizontal: 12 },
  backBtn:             { marginRight: 8, padding: 6 },
  headerInfo:          { flex: 1, flexDirection: 'row', alignItems: 'center' },
  avatar:              { width: 40, height: 40, borderRadius: 20, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center', position: 'relative' },
  avatarTxt:           { color: '#fff', fontSize: 15, fontWeight: 'bold' },
  onlineDot:           { position: 'absolute', width: 11, height: 11, borderRadius: 6, backgroundColor: '#10B981', borderWidth: 2, borderColor: '#fff', bottom: 0, right: 0 },
  headerName:          { fontSize: 17, fontWeight: '700', color: '#fff' },
  headerStatus:        { fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 1 },

  // v17 — indicateur "l'autre enregistre un vocal" (design sans texte)
  recIndicator:        { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3, height: 16 },
  recDot:              { width: 8, height: 8, borderRadius: 4, backgroundColor: '#FF4D4D' },
  recBars:             { flexDirection: 'row', alignItems: 'center', gap: 2, height: 14 },
  recBar:              { width: 3, height: 14, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.85)' },

  offlineBanner:       { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#EF4444', paddingHorizontal: 14, paddingVertical: 8 },
  offlineTxt:          { flex: 1, fontSize: 12, color: '#fff', fontWeight: '500' },

  msgs:                { flex: 1 },
  loadMoreBtn:         { alignSelf: 'center', marginBottom: 10, marginTop: 4, paddingHorizontal: 16, paddingVertical: 8, backgroundColor: '#fff', borderRadius: 20, flexDirection: 'row', alignItems: 'center', gap: 6, shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 4, elevation: 2 },
  loadMoreTxt:         { fontSize: 13, color: '#8A2BE2', fontWeight: '600' },

  bubble:              { maxWidth: '78%', paddingHorizontal: 12, paddingTop: 8, paddingBottom: 6, borderRadius: 18, marginBottom: 4 },
  bubbleMe:            { alignSelf: 'flex-end', backgroundColor: '#8A2BE2', borderBottomRightRadius: 4 },
  bubbleThem:          { alignSelf: 'flex-start', backgroundColor: '#fff', borderBottomLeftRadius: 4, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.08, shadowRadius: 2, elevation: 1 },
  bubbleFailed:        { borderWidth: 1, borderColor: '#FF6B6B' },
  bubbleFooter:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginTop: 3, gap: 3 },

  senderName:          { fontSize: 12, fontWeight: '700', color: '#E0D0FF', marginBottom: 3 },
  msgText:             { fontSize: 15, lineHeight: 21 },
  msgTextMe:           { color: '#fff' },
  msgTextThem:         { color: '#333' },
  msgDeleted:          { color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' },
  msgTime:             { fontSize: 11 },
  msgTimeMe:           { color: '#E0D0FF' },
  msgTimeThem:         { color: '#999' },
  statusText:          { fontSize: 11, color: '#E0D0FF' },

  editedLabel:         { fontSize: 10, fontStyle: 'italic' },
  editedLabelMe:       { color: 'rgba(224,208,255,0.8)' },
  editedLabelThem:     { color: '#bbb' },

  replyBlock:          { borderRadius: 8, padding: 8, marginBottom: 6, borderLeftWidth: 3, borderLeftColor: 'rgba(255,255,255,0.5)' },
  replyBlockMe:        { backgroundColor: 'rgba(255,255,255,0.15)' },
  replyBlockThem:      { backgroundColor: '#F5F0FF', borderLeftColor: '#8A2BE2' },
  replyAuthor:         { fontSize: 12, fontWeight: '700', color: '#8A2BE2', marginBottom: 2 },
  replyContent:        { fontSize: 12, color: '#555' },
  privateReplyWrap:    { flexDirection: 'row', alignItems: 'center' },
  retryBtn:            { flexDirection: 'row', alignItems: 'center', gap: 3 },
  retryTxt:            { fontSize: 10, color: '#FF6B6B', fontWeight: '600' },

  mediaImage:          { width: 200, height: 160, borderRadius: 10 },
  mediaFileWrap:       { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 4, paddingRight: 4 },
  mediaFileWrapMe:     {},
  mediaFileWrapThem:   {},
  mediaIconCircle:     { width: 38, height: 38, borderRadius: 19, justifyContent: 'center', alignItems: 'center' },
  mediaIconCircleMe:   { backgroundColor: 'rgba(255,255,255,0.2)' },
  mediaIconCircleThem: { backgroundColor: '#8A2BE2' },
  mediaFileName:       { fontSize: 13, flex: 1 },
  mediaFileNameMe:     { color: '#fff' },
  mediaFileNameThem:   { color: '#333' },

  audioWrap:           { flexDirection: 'row', alignItems: 'center', paddingVertical: 4, minWidth: 180 },
  audioWrapMe:         {},
  audioWrapThem:       {},

  emptyWrap:           { alignItems: 'center', paddingVertical: 80 },
  emptyText:           { fontSize: 14, color: '#999' },
  emptyTitle:          { fontSize: 18, fontWeight: 'bold', color: '#888', marginTop: 15 },
  emptySubtitle:       { fontSize: 14, color: '#aaa', marginTop: 6 },

  replyBar:            { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0E8FF', paddingHorizontal: 14, paddingVertical: 8, borderTopWidth: 1, borderTopColor: '#E0E0E0' },
  replyBarAuthor:      { fontSize: 12, fontWeight: '700', marginBottom: 1 },
  replyBarText:        { fontSize: 12, color: '#666' },

  editBar:             { flexDirection: 'row', alignItems: 'center', backgroundColor: '#ECFDF5', paddingHorizontal: 14, paddingVertical: 8, borderTopWidth: 1, borderTopColor: '#D1FAE5' },
  editBarIconWrap:     { width: 28, height: 28, borderRadius: 14, backgroundColor: '#D1FAE5', justifyContent: 'center', alignItems: 'center', marginRight: 8 },
  editBarLabel:        { fontSize: 12, fontWeight: '700', color: '#059669', marginBottom: 1 },
  editBarText:         { fontSize: 12, color: '#374151' },

  inputWrap:           { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 8, paddingVertical: 8, gap: 6, borderTopWidth: 1, borderTopColor: '#E0E0E0' },
  attachBtn:           { width: 40, height: 40, justifyContent: 'center', alignItems: 'center', borderRadius: 20 },
  input:               { flex: 1, backgroundColor: '#F5F5F5', borderRadius: 22, paddingHorizontal: 16, paddingVertical: 10, fontSize: 15, maxHeight: 110, outlineStyle: 'none' } as any,
  inputFull:           {},
  sendBtn:             { width: 42, height: 42, borderRadius: 21, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  sendBtnDisabled:     { backgroundColor: '#ccc' },
  sendBtnEdit:         { backgroundColor: '#059669' },
  micBtn:              { width: 42, height: 42, borderRadius: 21, backgroundColor: '#F0E8FF', justifyContent: 'center', alignItems: 'center' },

  recordCancelBtn:     { width: 42, height: 42, borderRadius: 21, backgroundColor: '#FEE2E2', justifyContent: 'center', alignItems: 'center' },
  recordingIndicator:  { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8 },
  recordingDot:        { width: 10, height: 10, borderRadius: 5, backgroundColor: '#DC2626' },
  recordingTimer:      { fontSize: 16, fontWeight: '700', color: '#DC2626' },
  recordingLabel:      { fontSize: 13, color: '#666' },

  previewPlayBtn:      { width: 42, height: 42, borderRadius: 21, backgroundColor: '#F0E8FF', justifyContent: 'center', alignItems: 'center' },
  previewSliderWrap:   { flex: 1, justifyContent: 'center', paddingHorizontal: 4 },
  previewTimeRow:      { flexDirection: 'row', justifyContent: 'space-between', marginTop: 2 },
  previewTimeText:     { fontSize: 11, color: '#999' },

  modalOverlay:        { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  actionSheet:         { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, paddingBottom: 16, paddingTop: 12 },
  actionItem:          { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 13, gap: 14 },
  actionIcon:          { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  actionTxt:           { fontSize: 16, color: '#111', fontWeight: '500' },
  actionSubTxt:        { fontSize: 12, color: '#999', marginTop: 1 },
  actionDivider:       { height: 1, backgroundColor: '#F0F0F0', marginHorizontal: 16, marginVertical: 4 },

  fsOverlay:           { flex: 1, backgroundColor: 'rgba(0,0,0,0.95)', justifyContent: 'center', alignItems: 'center' },
  fsImage:             { width: Dimensions.get('window').width, height: Dimensions.get('window').height * 0.85 },
  fsCloseBtn:          { position: 'absolute', top: 36, right: 20 },
});
